import { useEffect, useRef } from "react";
import { useTrans } from "../hooks";
import { useAuthData } from "../contexts";
import {
    Text,
    Avatar,
    HStack,
    VStack,
    Box,
    UseDisclosureReturn,
    IconButton,
} from "@chakra-ui/react";
import { AppIcon } from "./AppIcon";
import { AppTooltip } from "./AppTooltip";
import { AppOptionBoxItem, AppOptionsBox } from "./AppOptionsBox";
import { Timesheet, TimesheetType } from "../apis";
import { APPC, UTIL } from "../config";

export type UserTimesheetsType = {
    id: number;
    fullName?: string;
    imageName?: string | null;
    timesheets: Timesheet[];
};

interface AppTimesheetUserSelectorProps {
    data: UserTimesheetsType;
    deleteAlertState: UseDisclosureReturn;
    onAddTimesheet?: (data: UserTimesheetsType) => void;
    onRowEdit: (timesheet: Timesheet, timesheets: Timesheet[]) => void;
    onValidate: (ids: number[], isChecked: boolean) => void;
    setActionSelectId: (id: number) => void;
}

export const AppTimesheetUserSelector = ({
    data,
    deleteAlertState,
    onAddTimesheet,
    onRowEdit,
    onValidate,
    setActionSelectId,
}: AppTimesheetUserSelectorProps) => {
    const { t } = useTrans();
    const dayjs = UTIL.getDayJs();
    const { grant } = useAuthData();
    const timesheetIds = data.timesheets.map((ts) => ts.id);
    const ref = useRef<Record<number, HTMLInputElement | null>>({});
    const selectAllRef = useRef<HTMLInputElement | null>(null);

    const actionOptions: AppOptionBoxItem[] = [
        {
            key: "edit",
            label: t("cmn:option.edit"),
            iconName: "icl-pencil",
            isDisabled: grant.isCompanyDirector,
        },
        {
            key: "delete",
            label: t("cmn:option.delete"),
            iconName: "icl-trash",
            isDisabled: grant.isCompanyDirector,
        },
    ];

    const actionClickHandler = (key: string, timesheet: any) => {
        if (!timesheet) {
            return;
        }
        switch (key) {
            case "edit":
                onRowEdit(timesheet, data.timesheets);
                break;
            case "delete":
                setActionSelectId(timesheet.id);
                deleteAlertState.onOpen();
                break;
        }
    };

    const onSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        Object.values(ref.current).forEach((ele) => {
            if (ele) {
                ele.checked = isChecked;
            }
        });
        onValidate(timesheetIds, isChecked);
    };

    useEffect(() => {
        if (selectAllRef.current) {
            const allChecked = Object.values(ref.current).every(
                (ele) => ele?.checked
            );
            selectAllRef.current.checked = allChecked;
        }
    }, [data.timesheets]);

    return (
        <VStack w={"full"} maxW={"300px"}>
            <HStack
                width={"full"}
                bg={"var(--chakra-colors-primaryT95)"}
                boxShadow={"md"}
                borderRadius={"0.5rem"}
                px={3}
                py={2}
                flexWrap={"nowrap"}
                justifyContent={"space-between"}
            >
                <HStack>
                    <Avatar
                        size={"sm"}
                        name={data.fullName}
                        color={"var(--chakra-colors-primaryS10)"}
                        bgColor={"var(--chakra-colors-primaryT80)"}
                    />
                    <Text
                        textStyle={"mediumSm"}
                        color={"var(--chakra-colors-black)"}
                        noOfLines={1}
                    >
                        {data.fullName}
                    </Text>
                </HStack>
                {onAddTimesheet && !grant.isCompanyDirector && (
                    <Box>
                        <IconButton
                            aria-label="add-button"
                            variant={"unstyled"}
                            icon={<AppIcon name="icl-plus" w="1rem" />}
                            size="xs"
                            onClick={() => onAddTimesheet(data)}
                        />
                    </Box>
                )}
            </HStack>
            {data.timesheets.map((timesheet) => {
                return (
                    <HStack
                        key={timesheet.id}
                        width={"full"}
                        borderRadius={"0.5rem"}
                        px={3}
                        py={2}
                        flexWrap={"nowrap"}
                        border={"1px solid var(--chakra-colors-primaryT80)"}
                        justifyContent={"space-between"}
                        bg={
                            (timesheet.timesheetType as TimesheetType)
                                .isCountable
                                ? "var(--chakra-colors-successT90)"
                                : "var(--chakra-colors-destructiveT90)"
                        }
                    >
                        <HStack w={"full"}>
                            <input
                                type="checkbox"
                                ref={(ele) => (ref.current[timesheet.id] = ele)}
                                defaultChecked={timesheet.isValidated ?? false}
                                onChange={(e) => {
                                    onValidate(
                                        [timesheet.id],
                                        e.target.checked
                                    );
                                    const allChecked = Object.values(
                                        ref.current
                                    ).every((ele) => ele?.checked);
                                    if (selectAllRef.current) {
                                        selectAllRef.current.checked =
                                            allChecked;
                                    }
                                }}
                                disabled={grant.isCompanyDirector}
                            />
                            <AppTooltip label={"Work"}>
                                <Text
                                    w={"80px"}
                                    flexGrow={1}
                                    textStyle={"mediumSm"}
                                    color={"var(--chakra-colors-black)"}
                                    noOfLines={1}
                                >
                                    {
                                        (
                                            timesheet.timesheetType as TimesheetType
                                        ).name
                                    }
                                </Text>
                            </AppTooltip>
                            {timesheet.comment && (
                                <AppTooltip label={timesheet.comment}>
                                    <Box>
                                        <AppIcon
                                            name="icl-exclamation-circle"
                                            w="0.90rem"
                                        />
                                    </Box>
                                </AppTooltip>
                            )}
                            <Text
                                w={"95px"}
                                textStyle={"mediumSm"}
                                color={"var(--chakra-colors-black)"}
                                noOfLines={1}
                            >
                                {dayjs(
                                    (timesheet.startTime as string).replace(
                                        "+00:00",
                                        ""
                                    )
                                ).format(APPC.APP_TIME_FORMAT)}{" "}
                                -{" "}
                                {dayjs(
                                    (timesheet.endTime as string).replace(
                                        "+00:00",
                                        ""
                                    )
                                ).format(APPC.APP_TIME_FORMAT)}
                            </Text>
                        </HStack>
                        <AppOptionsBox
                            options={actionOptions}
                            data={timesheet}
                            onClick={actionClickHandler}
                        />
                    </HStack>
                );
            })}
            {data.timesheets.length > 0 && (
                <HStack
                    width={"full"}
                    borderRadius={"0.5rem"}
                    px={3}
                    py={2}
                    flexWrap={"nowrap"}
                    border={"1px solid var(--chakra-colors-primaryT80)"}
                    justifyContent={"space-between"}
                    h={"42px"}
                >
                    <HStack w={"full"}>
                        <input
                            type="checkbox"
                            ref={selectAllRef}
                            onChange={onSelectAll}
                            disabled={grant.isCompanyDirector}
                        />
                        <Text
                            textStyle={"mediumSm"}
                            color={"var(--chakra-colors-black)"}
                            noOfLines={1}
                            textAlign={"right"}
                        >
                            {t("com.AppTimesheetUserSelector:text.selectAll")}
                        </Text>
                    </HStack>
                </HStack>
            )}
        </VStack>
    );
};
